import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Grid,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { SmsService } from '../../../../service/smsService';
import { SmsTemplateService } from '../../../../service/smsTemplateService';
import { validLocationService } from '../../../../service/validLocationService';
import { toast } from 'react-toastify';
import BulkSmsProgress from './BulkSmsProgress';
import BulkSmsResults from './BulkSmsResults';

const FILTER_TYPES = {
    TOMORROW: 'appointments_tomorrow',
    NEXT_7_DAYS: 'appointments_next_7_days',
    CUSTOM_RANGE: 'appointments_custom_range'
};

const STEPS = ['Select Filter', 'Choose Template', 'Review & Send'];

export default function BulkSmsDialog({ open, onClose, onSuccess }) {
    // Stepper state
    const [activeStep, setActiveStep] = useState(0);

    // Step 1: Filter selection
    const [filterType, setFilterType] = useState(FILTER_TYPES.TOMORROW);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [storeId, setStoreId] = useState(null);
    const [locations, setLocations] = useState([]);
    const [validationLoading, setValidationLoading] = useState(false);
    const [validationResult, setValidationResult] = useState(null);

    // Step 2: Template selection
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateLoading, setTemplateLoading] = useState(false);

    // Progress & Results
    const [batchId, setBatchId] = useState(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    // Load locations on mount
    useEffect(() => {
        if (open) {
            loadLocations();
            setActiveStep(0);
            setError('');
            setBatchId(null);
        }
    }, [open]);

    // Load templates when step 2 is reached
    useEffect(() => {
        if (activeStep === 1) {
            loadTemplates();
        }
    }, [activeStep]);

    const loadLocations = async () => {
        try {
            const response = await validLocationService.getValidLocations();
            setLocations(response.data || []);
        } catch (err) {
            console.error('Failed to load locations:', err);
        }
    };

    const loadTemplates = async () => {
        setTemplateLoading(true);
        try {
            const response = await SmsTemplateService.getTemplates();
            setTemplates(response.data || []);
        } catch (err) {
            console.error('Failed to load templates:', err);
            setTemplates([]);
        } finally {
            setTemplateLoading(false);
        }
    };

    /**
     * Validate filters - count recipients and opted-out customers
     */
    const handleValidateFilters = async () => {
        if (!filterType) {
            setError('Please select a filter type');
            return;
        }

        // For custom range, require both dates
        if (filterType === FILTER_TYPES.CUSTOM_RANGE && (!filterStartDate || !filterEndDate)) {
            setError('Please specify start and end dates for custom range');
            return;
        }

        setValidationLoading(true);
        setError('');

        try {
            const payload = {
                filterType,
                storeId,
                ...(filterType === FILTER_TYPES.CUSTOM_RANGE && {
                    filterStartDate,
                    filterEndDate
                })
            };

            const response = await SmsService.validateBulkFilters(payload);

            if (response?.code === 0) {
                setValidationResult(response.data);
                setError('');
                // Auto-advance to step 2 on successful validation
                setActiveStep(1);
            } else {
                setError(response?.message || 'Validation failed');
            }
        } catch (err) {
            setError(err.message || 'Failed to validate filters');
            console.error('Validation error:', err);
        } finally {
            setValidationLoading(false);
        }
    };

    /**
     * Create bulk send and transition to sending
     */
    const handleCreateBulkSend = async () => {
        if (!selectedTemplateId) {
            setError('Please select a template');
            return;
        }

        setSending(true);
        setError('');

        try {
            const payload = {
                filterType,
                storeId,
                templateId: parseInt(selectedTemplateId),
                ...(filterType === FILTER_TYPES.CUSTOM_RANGE && {
                    filterStartDate,
                    filterEndDate
                })
            };

            const response = await SmsService.createBulkSend(payload);

            if (response?.code === 0) {
                setBatchId(response.data.batchId);
                // Proceed to progress/results view
                setActiveStep(2);
                toast.success('Bulk SMS queued - sending will begin shortly');
            } else {
                setError(response?.message || 'Failed to create bulk send');
            }
        } catch (err) {
            setError(err.message || 'Failed to create bulk send');
            console.error('Create send error:', err);
        } finally {
            setSending(false);
        }
    };

    const handleBack = () => {
        if (activeStep > 0 && !batchId) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleClose = () => {
        if (batchId) {
            // Ask if user wants to close while sending
            const confirmed = window.confirm('Bulk SMS is still processing. Close anyway? (batch will continue in background)');
            if (!confirmed) return;
        }
        onClose();
    };

    /**
     * Render Step 1: Filter Selection
     */
    const renderFilterStep = () => (
        <Stack spacing={3}>
            <FormControl fullWidth>
                <InputLabel>Filter Recipients By</InputLabel>
                <Select
                    value={filterType}
                    label="Filter Recipients By"
                    onChange={(e) => {
                        setFilterType(e.target.value);
                        setValidationResult(null);
                    }}
                >
                    <MenuItem value={FILTER_TYPES.TOMORROW}>Appointments Tomorrow</MenuItem>
                    <MenuItem value={FILTER_TYPES.NEXT_7_DAYS}>Appointments (Next 7 Days)</MenuItem>
                    <MenuItem value={FILTER_TYPES.CUSTOM_RANGE}>Custom Date Range</MenuItem>
                </Select>
            </FormControl>

            {/* Custom date range inputs */}
            {filterType === FILTER_TYPES.CUSTOM_RANGE && (
                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={filterStartDate}
                        onChange={(e) => setFilterStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={filterEndDate}
                        onChange={(e) => setFilterEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Stack>
            )}

            {/* Store/Location selection */}
            <FormControl fullWidth>
                <InputLabel>Location (Store)</InputLabel>
                <Select
                    value={storeId || ''}
                    label="Location (Store)"
                    onChange={(e) => {
                        setStoreId(e.target.value || null);
                        setValidationResult(null);
                    }}
                >
                    <MenuItem value="">All Locations</MenuItem>
                    {locations.map(loc => (
                        <MenuItem key={loc.id} value={loc.id}>
                            {loc.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Validation result preview */}
            {validationResult && (
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Box textAlign="center">
                                <Typography variant="h6" color="primary">
                                    {validationResult.validCount}
                                </Typography>
                                <Typography variant="caption">Will Receive SMS</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box textAlign="center">
                                <Typography variant="h6" color="error">
                                    {validationResult.optedOutCount}
                                </Typography>
                                <Typography variant="caption">Opted Out</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleValidateFilters}
                    disabled={validationLoading}
                    endIcon={validationLoading ? <CircularProgress size={20} /> : null}
                >
                    Next
                </Button>
            </Box>
        </Stack>
    );

    /**
     * Render Step 2: Template Selection
     */
    const renderTemplateStep = () => {
        const selectedTemplate = templates.find(t => t.id === parseInt(selectedTemplateId));
        const templatePreview = selectedTemplate
            ? `${selectedTemplate.header || ''}\n${selectedTemplate.body}\n${selectedTemplate.footer || ''}`
            : '';

        return (
            <Stack spacing={3}>
                <FormControl fullWidth disabled={templateLoading}>
                    <InputLabel>Select SMS Template</InputLabel>
                    <Select
                        value={selectedTemplateId}
                        label="Select SMS Template"
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                    >
                        <MenuItem value="">-- Choose a template --</MenuItem>
                        {templates.map(template => (
                            <MenuItem key={template.id} value={template.id}>
                                {template.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Template preview */}
                {selectedTemplate && (
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Template Preview:
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    p: 1.5,
                                    bgcolor: '#f9f9f9',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {templatePreview}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Character count: {templatePreview.length} (approx. {Math.ceil(templatePreview.length / 160)} SMS)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Note: Tokens like {'{patient_name}'} will be replaced with actual data before sending
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                    <Button onClick={handleBack}>Back</Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleCreateBulkSend}
                            disabled={sending || !selectedTemplateId}
                            endIcon={sending ? <CircularProgress size={20} /> : null}
                        >
                            Send to All
                        </Button>
                    </Box>
                </Box>
            </Stack>
        );
    };

    /**
     * Render Step 3: Sending & Results
     */
    const renderProgressStep = () => {
        return (
            <Stack spacing={2}>
                {batchId ? (
                    <>
                        <BulkSmsProgress batchId={batchId} />
                        <Divider sx={{ my: 1 }} />
                        <BulkSmsResults batchId={batchId} />
                    </>
                ) : (
                    <Alert severity="error">No batch ID - something went wrong</Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose}>Close</Button>
                </Box>
            </Stack>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
            <DialogTitle>Send Bulk SMS</DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {/* Stepper */}
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {STEPS.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Step content */}
                {activeStep === 0 && renderFilterStep()}
                {activeStep === 1 && renderTemplateStep()}
                {activeStep === 2 && renderProgressStep()}
            </DialogContent>
        </Dialog>
    );
}
